import type { ComponentType } from 'react';
import { Box, Button, Card, Chip, Link, Stack, Typography, type SvgIconProps } from '@mui/material';
import AutoGraphRounded from '@mui/icons-material/AutoGraphRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import LightbulbRounded from '@mui/icons-material/LightbulbRounded';
import SupervisorAccountRounded from '@mui/icons-material/SupervisorAccountRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import { Outlet, Link as RouterLink } from 'react-router';
import { AuroraBackground } from '@/components/AuroraBackground';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { FaiscaCredito } from '@/components/brand/FaiscaCredito';
import { ColorModeToggle } from '@/components/ColorModeToggle';
import { PageTransition } from '@/components/PageTransition';
import { brand } from '@/config/brand';
import { AuthChromeContext } from './authChrome';
import { IndependenceNote } from '@/components/IndependenceNote';

const FEATURES: { icon: ComponentType<SvgIconProps>; title: string; text: string }[] = [
  {
    icon: FactCheckRounded,
    title: 'Simulados no formato ENADE',
    text: 'Questões objetivas e discursivas, revisadas por professores do ensino superior de cada área, com cronômetro e respostas salvas sozinhas.',
  },
  {
    icon: LightbulbRounded,
    title: 'Gabarito e resolução',
    text: 'Logo depois de cada questão ou no final da prova, como a coordenação definir.',
  },
  {
    icon: AutoGraphRounded,
    title: 'Seu desempenho em gráficos',
    text: 'Evolução entre as tentativas, pontos fortes e o que reforçar em cada área.',
  },
  {
    icon: SupervisorAccountRounded,
    title: 'Para a coordenação',
    text: 'Libere avaliações por aluno ou turma e acompanhe cada curso e cada estudante.',
  },
];

/**
 * Public entry (sign in, sign up, password reset): what the platform does on one side, the
 * form on the other. On phones the form comes right after the headline, features below.
 */
export function LandingLayout() {
  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <AuroraBackground />
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          maxWidth: 1180,
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 5 },
          py: 1.5,
        }}
      >
        <BrandLogo size="medium" />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Button
            component={RouterLink}
            to="/enem/conhecer"
            color="inherit"
            sx={{ fontWeight: 600 }}
          >
            Vai fazer o ENEM?
          </Button>
          <Button
            component={RouterLink}
            to="/planos"
            color="inherit"
            sx={{ fontWeight: 600, display: { xs: 'none', sm: 'inline-flex' } }}
          >
            Planos para instituições
          </Button>
          <ColorModeToggle />
        </Stack>
      </Stack>
      <Box
        component="main"
        sx={{
          flex: 1,
          width: '100%',
          maxWidth: 1180,
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 2, md: 4 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.15fr) minmax(360px, 440px)' },
          gridTemplateAreas: {
            xs: '"hero" "form" "features"',
            md: '"hero form" "features form"',
          },
          columnGap: { md: 8 },
          rowGap: { xs: 3, md: 4 },
          alignContent: 'center',
          alignItems: 'start',
        }}
      >
        <Box sx={{ gridArea: 'hero', alignSelf: { md: 'end' } }}>
          <Chip
            size="small"
            variant="soft"
            color="lime"
            icon={<TrendingUpRounded />}
            label="Avaliações para instituições de ensino"
            sx={{ mb: 2 }}
          />
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.125rem' },
              lineHeight: 1.08,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              fontVariationSettings: "'ROND' 100",
              maxWidth: 620,
            }}
          >
            Pratique, veja onde errou e{' '}
            <Box component="span" sx={{ color: 'primary.main' }}>
              acompanhe sua evolução.
            </Box>
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mt: 2, fontSize: { xs: '1rem', md: '1.125rem' }, maxWidth: 560 }}
          >
            {brand.name ?? 'A plataforma'} reúne os simulados e as avaliações que a sua coordenação
            libera, com gabarito comentado e gráficos do seu desempenho.
          </Typography>
        </Box>

        <Box sx={{ gridArea: 'form', alignSelf: { md: 'center' } }}>
          <PageTransition>
            <Card
              sx={(theme) => ({
                width: '100%',
                maxWidth: 440,
                mx: 'auto',
                p: { xs: 3, sm: 4 },
                borderRadius: 6,
                boxShadow: `0 24px 64px -24px ${theme.alpha('#2A1263', 0.25)}`,
                ...theme.applyStyles('dark', {
                  boxShadow: `0 24px 64px -24px ${theme.alpha('#000', 0.6)}`,
                }),
              })}
            >
              <AuthChromeContext.Provider value={{ showLogo: false }}>
                <Outlet />
              </AuthChromeContext.Provider>
            </Card>
          </PageTransition>
        </Box>

        <Box sx={{ gridArea: 'features', alignSelf: { md: 'start' } }}>
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <Stack component="li" key={title} direction="row" spacing={1.5}>
                <Box
                  sx={(theme) => ({
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: 3,
                    display: 'grid',
                    placeItems: 'center',
                    color: theme.vars.palette.primary.main,
                    bgcolor: theme.alpha(theme.vars.palette.primary.main, 0.1),
                  })}
                >
                  <Icon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" component="h2">
                    {title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {text}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Box>
          <ResultPreview />
        </Box>
      </Box>
      <IndependenceNote sx={{ mt: 2 }} />
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
        © {new Date().getFullYear()}
        {brand.name ? ` ${brand.name}` : ''} ·{' '}
        <Link href="/privacidade.html" color="inherit">
          Privacidade
        </Link>
      </Typography>
      <FaiscaCredito />
    </Box>
  );
}

const PREVIEW_AREAS = [
  { label: 'Formação Geral', value: 78 },
  { label: 'Engenharia de software', value: 92 },
  { label: 'Redes de computadores', value: 55 },
];

/** A glimpse of what a student sees after a quiz. Illustration only. */
function ResultPreview() {
  const size = 76;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <Card
      aria-hidden
      sx={(theme) => ({
        display: { xs: 'none', md: 'block' },
        mt: 4,
        p: 2.5,
        maxWidth: 520,
        borderRadius: 5,
        transform: 'rotate(-1.2deg)',
        boxShadow: `0 20px 48px -28px ${theme.alpha('#2A1263', 0.35)}`,
        ...theme.applyStyles('dark', {
          boxShadow: `0 20px 48px -28px ${theme.alpha('#000', 0.7)}`,
        }),
      })}
    >
      <Stack direction="row" spacing={2.5} sx={{ alignItems: 'center' }}>
        <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
          <Box
            component="svg"
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            sx={{ transform: 'rotate(-90deg)', display: 'block' }}
          >
            <Box
              component="circle"
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              strokeWidth={stroke}
              sx={(t) => ({ stroke: t.alpha(t.vars.palette.success.main, 0.16) })}
            />
            <Box
              component="circle"
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={c}
              strokeDashoffset={c * (1 - 0.82)}
              sx={(t) => ({ stroke: t.vars.palette.success.main })}
            />
          </Box>
          <Typography
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              fontWeight: 700,
              fontSize: '1.125rem',
            }}
          >
            82%
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.25 }}>
            <Typography variant="subtitle2" noWrap>
              Simulado ENADE · 2ª tentativa
            </Typography>
            <Chip size="small" variant="soft" color="success" label="+14%" />
          </Stack>
          <Stack spacing={1}>
            {PREVIEW_AREAS.map((area) => (
              <Box
                key={area.label}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) 90px 36px',
                  gap: 1.25,
                  alignItems: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary" noWrap>
                  {area.label}
                </Typography>
                <Box
                  sx={(t) => ({
                    height: 8,
                    borderRadius: 99,
                    bgcolor: t.alpha(t.vars.palette.primary.main, 0.14),
                    overflow: 'hidden',
                  })}
                >
                  <Box
                    sx={(t) => ({
                      width: `${area.value}%`,
                      height: '100%',
                      borderRadius: 99,
                      bgcolor: t.vars.palette.primary.main,
                    })}
                  />
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>
                  {area.value}%
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
}
